import { Comment, CommentProfileCardTooltip } from "@etframework/ui.components";
import "moment/dist/locale/es-us";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { formatDateFromNow } from "../../utils/helpers";
import {
  useUpdateComment,
  useDeleteComment,
  useCreateComment,
} from "../../data/mutations/commentMutations";
import { CommentEditor } from "@etframework/ui.components";
import { useCallback, useEffect } from "react";
import { DateFormats } from "../../types/DateFormats";
import { useDueDatesAuth } from "../../hooks/useDueDatesAuth";
import { Fragment } from "react";
import { useUsers } from "../../data/queries/userQueries";
import { toast } from "react-toastify";
import { detach } from "@syncfusion/ej2-base";
import { useRef } from "react";

export const Comments = ({ comments, task, refetchComments }) => {
  const { t, i18n } = useTranslation(["Overlay", "Shared"]);
  const { UserId } = useDueDatesAuth();

  const deletedComments = useRef([]);

  const { mutate: updateComment } = useUpdateComment();
  const { mutate: deleteComment } = useDeleteComment(() => {
    refetchComments?.();
  });
  const { mutate: createComment } = useCreateComment();

  const { data: users } = useUsers();

  const createCommentId = "dd_task-overlay_comments_create-comment-rte-input";
  const fields = { value: "Id", text: "FullName", displayValue: "Email" };

  const handleCreate = useCallback(
    async (value) => {
      createComment(
        {
          comment: {
            TaskId: task?.Id,
            Text: value,
            UserId: UserId,
          },
        },
        {
          onError: () => {
            toast.error(t("ErrorCreatingComment"));
            refetchComments?.();
          },
        }
      );
    },
    [task?.Id, createComment, UserId]
  );

  const handleEdit = async (commentId, newValue) => {
    updateComment(
      {
        comment: {
          Id: commentId,
          Text: newValue,
        },
      },
      {
        onError: () => {
          toast.error(t("ErrorUpdatingComment"));
          refetchComments?.();
        },
      }
    );
  };

  const handleDelete = async (commentId) => {
    if (deletedComments.current.includes(commentId)) return;
    deletedComments.current.push(commentId);

    deleteComment({
      commentId: commentId,
    });
  };

  useEffect(() => {
    // I added this to update the comment count in the div element with class "comment-count"
    // This is a workaround because if I invaldiate the query, all the component re-renders. -KV 4/4/2025
    if (comments) {
      const divElement = document.querySelector(".comment-count");
      if (divElement)
        divElement.innerHTML = comments?.length > 99 ? "99+" : comments?.length;
    }
  }, [comments]);

  const onAfterPasteCleanup = (args) => {
    const divElement = document.createElement("div");
    divElement.innerHTML = args.value;
    var divs = divElement.querySelectorAll("div");
    divs.forEach((div) => {
      detach(div);
    });
    args.value = divElement.innerHTML;
  };

  return (
    <div className="d-flex flex-column">
      <CommentProfileCardTooltip dataSource={users} fields={fields}>
        <div className={`task-comments-rte-container px-2 my-3`}>
          <CommentEditor
            id={createCommentId}
            emptyOnEnter={true}
            placeholder={t("CommentPlaceholder")}
            onEnter={handleCreate}
            onCancel={() => {}}
            actionName={t("CommentBtn")}
            initialHeightClass="short"
            lang={i18n.language}
            mentions={true}
            mentionProps={{
              dataSource: users,
              fields: fields,
            }}
            afterPasteCleanup={onAfterPasteCleanup}
            pasteCleanupSettings={{
              keepFormat: false,
              plainText: true,
            }}
            toolbarSettingsItems={["Image"]}
            insertImageSettings={{
              saveFormat: "Base64", // or SaveFormat.Base64 in some contexts
            }}
          />
        </div>
        <div className="task-comments">
          {comments?.length > 0 ? (
            comments?.map((comment, index) => (
              <Fragment key={comment?.Id}>
                <Comment
                  id={"comment-" + index}
                  text={comment?.Text}
                  username={comment?.UserFullName}
                  createdDateString={formatDateFromNow(
                    comment?.CreatedDate,
                    i18n.language,
                    DateFormats.ShortDateTimeFormat.momentJsFormat
                  )}
                  createdDate={moment.utc(comment?.CreatedDate).toDate()}
                  lang={i18n.language}
                  allowEdit={UserId === comment?.UserId}
                  onEdit={(value) => {
                    handleEdit(comment?.Id, value);
                  }}
                  onDelete={() => {
                    handleDelete(comment?.Id);
                  }}
                  mentions={true}
                  editorProps={{
                    maxLength: 500,
                    toolbarSettingsItems: ["Image"],
                    insertImageSettings: { saveFormat: "Base64" }, // or SaveFormat.Base64 in some contexts
                  }}
                  mentionsDataSource={users}
                  mentionsFields={fields}
                />
              </Fragment>
            ))
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center h-100 empty-comment">
              <div className="d-flex flex-column justify-content-center align-items-center h-100">
                <i className="fa-light fa-comments empty-comment-icon"></i>
                <span className="no-comments mt-4">{t("NoComments")}</span>
                <span className="">{t("Use@ToMention")}</span>
              </div>
            </div>
          )}
        </div>
      </CommentProfileCardTooltip>
    </div>
  );
};
